import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Ban, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminListBanned, adminUnbanUser } from "@/lib/admin-security.functions";

export const Route = createFileRoute("/_authenticated/admin/bans")({
  head: () => ({ meta: [{ title: "Bans — Admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <BansPage />
    </AdminOnly>
  ),
});

function BansPage() {
  const qc = useQueryClient();
  const runList = useServerFn(adminListBanned);
  const runUnban = useServerFn(adminUnbanUser);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-banned"],
    queryFn: () => runList(),
  });

  const rows = (data?.banned ?? []) as Array<{
    id: string; email: string; full_name: string | null;
    ban_reason: string | null; banned_at: string | null; banned_by: string | null;
  }>;

  const unban = useMutation({
    mutationFn: (id: string) => runUnban({ data: { userId: id, resetDevices: true } }),
    onSuccess: () => { toast.success("Unbanned"); qc.invalidateQueries({ queryKey: ["admin-banned"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Ban}
        title="Banned users"
        subtitle={`${rows.length} user${rows.length === 1 ? "" : "s"} currently banned.`}
        tone="text-red-500"
        glow="hsl(0 84% 60% / 0.30)"
      />


      <div className="overflow-hidden rounded-2xl border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
            No banned users. Everything looks good.
          </div>
        ) : (
          <div className="w-full overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-muted/40 text-start text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Banned</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link to="/admin/users/$id" params={{ id: u.id }} className="hover:underline">
                      <div className="font-medium">{u.full_name ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{u.ban_reason ?? "—"}</td>
                  <td className="px-4 py-3 text-[11px] text-muted-foreground">{u.banned_at ? new Date(u.banned_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-end">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => unban.mutate(u.id)} disabled={unban.isPending}>
                      <ShieldCheck className="me-1 h-3 w-3" /> Unban
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
